import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { Users, CalendarDays, DollarSign, PlusCircle, XCircle, Trash2, Edit } from 'lucide-react';
import Swal from 'sweetalert2';
import { useAuth } from '../hooks/useAuth';
import PayrollView from './PayrollView';

const DAYS_OF_WEEK = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

const DEFAULT_HORARIO = {
    activo: false,
    hora_inicio: '10:00',
    hora_fin: '18:00',
    inicio_almuerzo: '14:00',
    fin_almuerzo: '15:00',
    descansos: [] // Hasta 2 bloques: [{ inicio: 'HH:MM', fin: 'HH:MM' }]
};

const StaffDashboard = () => {
    const { currentUser } = useAuth();
    const role = currentUser?.role?.toUpperCase() || '';
    const isSupervisor = role === 'SUPERVISOR';
    const isAdminOrGerente = role === 'ADMIN' || role === 'GERENTE';
    const isATC = role === 'ATENCIÓN AL CLIENTE';
    // isSupervisorATC: empleado ATC con privilegio de supervisor
    const [isSupervisorATC, setIsSupervisorATC] = useState(false);
    // Detect supervisor department from their personal record
    const [supervisorDep, setSupervisorDep] = useState(null);
    const [configOperativa, setConfigOperativa] = useState({ inicio_jornada: '10:00', cierre_jornada: '23:00' });

    const [activeTab, setActiveTab] = useState('horario_maestro'); // 'horario_maestro', 'equipo', 'nomina'
    const [personal, setPersonal] = useState([]);
    const [asistencias, setAsistencias] = useState([]);
    
    // Modals
    const [showStaffModal, setShowStaffModal] = useState(false);
    const [editingStaffId, setEditingStaffId] = useState(null);

    // Form: Nuevo Miembro
    const [staffNombre, setStaffNombre] = useState('');
    const [staffEmail, setStaffEmail] = useState('');
    const [staffRol, setStaffRol] = useState('Gestor de Cuentas');
    const [staffDepartamento, setStaffDepartamento] = useState('Gestión de Cuentas');
    const [staffSalario, setStaffSalario] = useState('');
    const [staffCorreoBinance, setStaffCorreoBinance] = useState('');
    const [staffBono, setStaffBono] = useState('');

    useEffect(() => {
        const qPersonal = query(collection(db, 'personal'), orderBy('nombre', 'asc'));
        const unsubP = onSnapshot(qPersonal, (snap) => setPersonal(snap.docs.map(d => ({ id: d.id, ...d.data() }))));

        const qAsistencias = query(collection(db, 'asistencia_logs'), orderBy('createdAt', 'desc'));
        const unsubA = onSnapshot(qAsistencias, (snap) => setAsistencias(snap.docs.map(d => ({ id: d.id, ...d.data() }))));

        // Cargar config de jornada global
        const unsubOp = onSnapshot(doc(db, 'config_operativa', 'main'), snap => {
            if (snap.exists()) setConfigOperativa(prev => ({ ...prev, ...snap.data() }));
        });

        // Detectar si el usuario es ATC con privilegio de supervisor
        if (currentUser?.uid) {
            onSnapshot(doc(db, 'users', currentUser.uid), snap => {
                if (snap.exists()) setIsSupervisorATC(!!snap.data().isSupervisor);
            });
        }

        // Si es supervisor o ATC-supervisor, detectar su departamento
        if ((isSupervisor || isATC) && currentUser?.email) {
            const q = query(collection(db, 'personal'));
            onSnapshot(q, snap => {
                const found = snap.docs.find(d => d.data().email === currentUser.email);
                if (found) setSupervisorDep(found.data().rol);
            });
        }

        return () => { unsubP(); unsubA(); unsubOp(); };
    }, [isSupervisor, isATC, currentUser]);

    // --- GESTIÓN EQUIPO ---
    const handleEditStaffClick = (staff) => {
        setEditingStaffId(staff.id);
        setStaffNombre(staff.nombre);
        setStaffEmail(staff.email || '');
        setStaffRol(staff.rol);
        setStaffDepartamento(staff.departamento || 'Gestión de Cuentas');
        setStaffSalario(staff.salario_base?.toString() || '0');
        setStaffCorreoBinance(staff.correo_binance || '');
        setStaffBono((staff.bono_aprobado || 0).toString());
        setShowStaffModal(true);
    };

    const handleOpenNewStaffModal = () => {
        setEditingStaffId(null);
        setStaffNombre(''); setStaffEmail(''); setStaffSalario(''); setStaffCorreoBinance(''); setStaffBono('');
        setStaffRol('Gestor de Cuentas');
        setStaffDepartamento('Gestión de Cuentas');
        setShowStaffModal(true);
    };

    const handleSaveStaff = async (e) => {
        e.preventDefault();
        try {
            const initialHorario = Array(7).fill(DEFAULT_HORARIO);
            if (editingStaffId) {
                await updateDoc(doc(db, 'personal', editingStaffId), {
                    nombre: staffNombre,
                    email: staffEmail.toLowerCase(),
                    rol: staffRol,
                    departamento: staffDepartamento,
                    salario_base: Number(staffSalario),
                    correo_binance: staffCorreoBinance,
                    bono_aprobado: Number(staffBono) || 0,
                });
                Swal.fire({ title: 'Actualizado', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
            } else {
                await addDoc(collection(db, 'personal'), {
                    nombre: staffNombre,
                    email: staffEmail.toLowerCase(),
                    rol: staffRol,
                    departamento: staffDepartamento,
                    salario_base: Number(staffSalario),
                    correo_binance: staffCorreoBinance,
                    bono_aprobado: Number(staffBono) || 0,
                    estado: 'activo',
                    horario_maestro: initialHorario
                });
                Swal.fire({ title: 'Añadido', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
            }
            setShowStaffModal(false);
        } catch (error) {
            Swal.fire('Error', 'No se pudo guardar el personal', 'error');
        }
    };

    const handleDeleteStaff = async (id) => {
        if(window.confirm('¿Eliminar a este miembro del equipo?')) {
            await deleteDoc(doc(db, 'personal', id));
        }
    };

    // --- HORARIO MAESTRO ---
    const handleUpdateHorario = async (staffId, dayIndex, field, value) => {
        try {
            const staff = personal.find(p => p.id === staffId);
            const newHorario = [...(staff.horario_maestro || Array(7).fill(DEFAULT_HORARIO))];
            if (!newHorario[dayIndex]) newHorario[dayIndex] = { ...DEFAULT_HORARIO };
            else newHorario[dayIndex] = { ...newHorario[dayIndex] };
            newHorario[dayIndex][field] = value;
            await updateDoc(doc(db, 'personal', staffId), { horario_maestro: newHorario });
        } catch (error) {
            console.error('Error updating horario', error);
        }
    };

    const handleUpdateDescanso = async (staffId, dayIndex, descansoIdx, subField, value) => {
        try {
            const staff = personal.find(p => p.id === staffId);
            const newHorario = [...(staff.horario_maestro || Array(7).fill(DEFAULT_HORARIO))];
            if (!newHorario[dayIndex]) newHorario[dayIndex] = { ...DEFAULT_HORARIO };
            else newHorario[dayIndex] = { ...newHorario[dayIndex] };
            const descansos = [...(newHorario[dayIndex].descansos || [])];
            descansos[descansoIdx] = { ...descansos[descansoIdx], [subField]: value };
            newHorario[dayIndex].descansos = descansos;
            await updateDoc(doc(db, 'personal', staffId), { horario_maestro: newHorario });
        } catch (error) { console.error('Error updating descanso', error); }
    };

    const handleAddDescanso = async (staffId, dayIndex) => {
        try {
            const staff = personal.find(p => p.id === staffId);
            const newHorario = [...(staff.horario_maestro || Array(7).fill(DEFAULT_HORARIO))];
            if (!newHorario[dayIndex]) newHorario[dayIndex] = { ...DEFAULT_HORARIO };
            else newHorario[dayIndex] = { ...newHorario[dayIndex] };
            const descansos = [...(newHorario[dayIndex].descansos || [])];
            if (descansos.length >= 2) return; // Máximo 2
            descansos.push({ inicio: '11:30', fin: '11:45' });
            newHorario[dayIndex].descansos = descansos;
            await updateDoc(doc(db, 'personal', staffId), { horario_maestro: newHorario });
        } catch (error) { console.error('Error adding descanso', error); }
    };

    const handleRemoveDescanso = async (staffId, dayIndex, descansoIdx) => {
        try {
            const staff = personal.find(p => p.id === staffId);
            const newHorario = [...(staff.horario_maestro || Array(7).fill(DEFAULT_HORARIO))];
            if (!newHorario[dayIndex]) newHorario[dayIndex] = { ...DEFAULT_HORARIO };
            else newHorario[dayIndex] = { ...newHorario[dayIndex] };
            const descansos = [...(newHorario[dayIndex].descansos || [])];
            descansos.splice(descansoIdx, 1);
            newHorario[dayIndex].descansos = descansos;
            await updateDoc(doc(db, 'personal', staffId), { horario_maestro: newHorario });
        } catch (error) { console.error('Error removing descanso', error); }
    };

    // Cobertura Algorithm — usa departamento en vez de rol para filtrar
    const checkCoverage = (dayIndex, filterDepartamento) => {
        const startHour = parseInt(configOperativa.inicio_jornada.split(':')[0]);
        const endHour = parseInt(configOperativa.cierre_jornada.split(':')[0]);
        let hasCoverage = true;
        let missedHours = [];

        for (let hour = startHour; hour < endHour; hour++) {
            let covered = false;
            personal.forEach(p => {
                const dep = p.departamento || p.rol;
                if (filterDepartamento && dep !== filterDepartamento) return;
                const hs = p.horario_maestro?.[dayIndex];
                if (!hs || !hs.activo) return;
                const startH = parseInt(hs.hora_inicio.split(':')[0]);
                const endH = parseInt(hs.hora_fin.split(':')[0]);
                const lunchStartH = parseInt(hs.inicio_almuerzo.split(':')[0]);
                const lunchEndH = parseInt(hs.fin_almuerzo.split(':')[0]);
                // Verificar también los bloques de descanso
                const enDescanso = (hs.descansos || []).some(d => {
                    const ds = parseInt(d.inicio.split(':')[0]);
                    const de = parseInt(d.fin.split(':')[0]);
                    return hour >= ds && hour < de;
                });
                if (hour >= startH && hour < endH) {
                    if (!(hour >= lunchStartH && hour < lunchEndH) && !enDescanso) covered = true;
                }
            });
            if (!covered) { hasCoverage = false; missedHours.push(hour); }
        }
        return { hasCoverage, missedHours };
    };

    // --- NÓMINA ---

    const renderHorarioBlock = (title, filterDepartamento, accentColor = 'indigo') => {
        // Privacidad: solo puede ver su propio departamento si es supervisor/ATC-supervisor
        const isRestrictedSupervisor = (isSupervisor || isSupervisorATC) && !isAdminOrGerente;
        if (isRestrictedSupervisor && supervisorDep && supervisorDep !== filterDepartamento) return null;
        // ATC sin privilegios no ve el horario
        if (isATC && !isSupervisorATC) return null;

        // Filtrar por departamento (con fallback al campo rol para registros viejos)
        const filteredStaff = personal.filter(p => {
            const dep = p.departamento || p.rol;
            return dep === filterDepartamento;
        });
        if (filteredStaff.length === 0 && !isAdminOrGerente) return null;

        const colorMap = {
            indigo: { border: 'border-indigo-500/50', badge: 'bg-indigo-500/20 text-indigo-300', title: 'text-indigo-400', bar: 'border-l-indigo-500' },
            cyan:   { border: 'border-cyan-500/50',   badge: 'bg-cyan-500/20 text-cyan-300',   title: 'text-cyan-400',   bar: 'border-l-cyan-500'   },
            purple: { border: 'border-purple-500/50', badge: 'bg-purple-500/20 text-purple-300', title: 'text-purple-400', bar: 'border-l-purple-500' },
        };
        const c = colorMap[accentColor] || colorMap.indigo;

        return (
            <div className="mb-10">
                <div className={`flex items-center gap-3 mb-5 pl-4 border-l-4 ${c.bar}`}>
                    <h2 className={`text-xl font-bold ${c.title}`}>{title}</h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${c.badge}`}>{filteredStaff.length} empleado{filteredStaff.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="grid grid-cols-1 xl:grid-cols-7 gap-3">
                    {DAYS_OF_WEEK.map((dayName, dayIdx) => {
                        const coverage = checkCoverage(dayIdx, filterDepartamento);
                        const isToday = dayIdx === new Date().getDay();
                        return (
                            <div key={dayIdx} className={`bg-slate-800 rounded-xl border ${isToday ? c.border + ' shadow-lg' : 'border-slate-700'} flex flex-col h-full overflow-hidden`}>
                                <div className="bg-slate-900/60 p-3 text-center border-b border-slate-700">
                                    <p className={`text-xs font-bold uppercase tracking-wider mb-0.5 ${isToday ? c.title : 'text-slate-400'}`}>{dayName}</p>
                                    {coverage.hasCoverage ? (
                                        <span className="text-[9px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded-full font-bold">✓ Cobertura</span>
                                    ) : (
                                        <span className="text-[9px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded-full font-bold" title={`Brechas: ${coverage.missedHours.map(h => h+':00').join(', ')}`}>⚠ Brecha</span>
                                    )}
                                </div>
                                <div className="p-2 flex-1 space-y-2">
                                    {filteredStaff.map(staff => {
                                        const h = staff.horario_maestro?.[dayIdx] || DEFAULT_HORARIO;
                                        return (
                                            <div key={staff.id} className={`rounded-lg border transition-all ${h.activo ? 'bg-slate-900/60 border-slate-600' : 'bg-slate-900/20 border-slate-700/40 opacity-60'}`}>
                                                {/* Header empleado */}
                                                <div className="flex justify-between items-center px-2 pt-2 pb-1">
                                                    <span className={`text-xs font-bold truncate pr-1 ${h.activo ? 'text-white' : 'text-slate-500'}`}>{staff.nombre}</span>
                                                    {isAdminOrGerente && (
                                                        <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                                                            <input type="checkbox" className="sr-only peer" checked={h.activo} onChange={e => handleUpdateHorario(staff.id, dayIdx, 'activo', e.target.checked)} />
                                                            <div className="w-7 h-4 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-emerald-500"></div>
                                                        </label>
                                                    )}
                                                </div>

                                                {h.activo && (
                                                    <div className="px-2 pb-2 space-y-1.5">
                                                        {/* Bloque Turno */}
                                                        <div className="flex items-center gap-1 bg-emerald-500/10 rounded px-1.5 py-1">
                                                            <span className="text-[9px] text-emerald-500 font-bold w-8">TURNO</span>
                                                            {isAdminOrGerente ? (
                                                                <>
                                                                    <input type="time" value={h.hora_inicio} min={configOperativa.inicio_jornada} max={configOperativa.cierre_jornada} onChange={e => handleUpdateHorario(staff.id, dayIdx, 'hora_inicio', e.target.value)} className="bg-transparent text-emerald-300 text-[10px] font-mono w-14 focus:outline-none" />
                                                                    <span className="text-slate-500 text-[9px]">→</span>
                                                                    <input type="time" value={h.hora_fin} min={configOperativa.inicio_jornada} max={configOperativa.cierre_jornada} onChange={e => handleUpdateHorario(staff.id, dayIdx, 'hora_fin', e.target.value)} className="bg-transparent text-emerald-300 text-[10px] font-mono w-14 focus:outline-none" />
                                                                </>
                                                            ) : (
                                                                <span className="text-emerald-300 text-[10px] font-mono">{h.hora_inicio} → {h.hora_fin}</span>
                                                            )}
                                                        </div>
                                                        {/* Bloque Lunch */}
                                                        <div className="flex items-center gap-1 bg-amber-500/10 rounded px-1.5 py-1">
                                                            <span className="text-[9px] text-amber-500 font-bold w-8">LUNCH</span>
                                                            {isAdminOrGerente ? (
                                                                <>
                                                                    <input type="time" value={h.inicio_almuerzo} onChange={e => handleUpdateHorario(staff.id, dayIdx, 'inicio_almuerzo', e.target.value)} className="bg-transparent text-amber-300 text-[10px] font-mono w-14 focus:outline-none" />
                                                                    <span className="text-slate-500 text-[9px]">→</span>
                                                                    <input type="time" value={h.fin_almuerzo} onChange={e => handleUpdateHorario(staff.id, dayIdx, 'fin_almuerzo', e.target.value)} className="bg-transparent text-amber-300 text-[10px] font-mono w-14 focus:outline-none" />
                                                                </>
                                                            ) : (
                                                                <span className="text-amber-300 text-[10px] font-mono">{h.inicio_almuerzo} → {h.fin_almuerzo}</span>
                                                            )}
                                                        </div>
                                                         {/* Bloques Descanso */}
                                                        <div className="space-y-1">
                                                            {(h.descansos || []).map((d, di) => (
                                                                <div key={di} className="flex items-center gap-1 bg-cyan-500/10 rounded px-1.5 py-1">
                                                                    <span className="text-[9px] text-cyan-500 font-bold w-8">B{di + 1}</span>
                                                                    {isAdminOrGerente ? (
                                                                        <>
                                                                            <input type="time" value={d.inicio} onChange={e => handleUpdateDescanso(staff.id, dayIdx, di, 'inicio', e.target.value)} className="bg-transparent text-cyan-300 text-[10px] font-mono w-14 focus:outline-none" />
                                                                            <span className="text-slate-500 text-[9px]">→</span>
                                                                            <input type="time" value={d.fin} onChange={e => handleUpdateDescanso(staff.id, dayIdx, di, 'fin', e.target.value)} className="bg-transparent text-cyan-300 text-[10px] font-mono w-14 focus:outline-none" />
                                                                            <button type="button" onClick={() => handleRemoveDescanso(staff.id, dayIdx, di)} className="ml-auto text-red-400 hover:text-red-300 text-[9px] px-1">✕</button>
                                                                        </>
                                                                    ) : (
                                                                        <span className="text-cyan-300 text-[10px] font-mono">{d.inicio} → {d.fin}</span>
                                                                    )}
                                                                </div>
                                                            ))}
                                                            {isAdminOrGerente && (h.descansos || []).length < 2 && (
                                                                <button type="button" onClick={() => handleAddDescanso(staff.id, dayIdx)} className="w-full text-[9px] text-cyan-600 hover:text-cyan-400 border border-dashed border-cyan-800 hover:border-cyan-600 rounded py-0.5 transition-colors mt-0.5">
                                                                    + Descanso
                                                                </button>
                                                            )}
                                                            {(h.descansos || []).length === 0 && !isAdminOrGerente && (
                                                                <span className="text-[9px] text-slate-600 italic">Sin descansos</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {filteredStaff.length === 0 && (
                                        <p className="text-[11px] text-slate-600 italic text-center py-4">Sin empleados en esta sección</p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen text-slate-100 font-sans">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white tracking-tight m-0">Personal y Turnos</h1>
                    <p className="text-sm text-slate-400 mt-1">Gestión de equipo, plantillas semanales y nómina</p>
                </div>
            </div>

            {/* TABS */}
            <div className="flex border-b border-slate-700/50 mb-6 gap-2 overflow-x-auto">
                <button onClick={() => setActiveTab('horario_maestro')} className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors border-b-2 whitespace-nowrap ${activeTab === 'horario_maestro' ? 'border-indigo-500 text-indigo-400' : 'border-transparent text-slate-400 hover:text-slate-300'}`}>
                    <CalendarDays size={18} /> Horario Maestro Semanal
                </button>
                <button onClick={() => setActiveTab('equipo')} className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors border-b-2 whitespace-nowrap ${activeTab === 'equipo' ? 'border-emerald-500 text-emerald-400' : 'border-transparent text-slate-400 hover:text-slate-300'}`}>
                    <Users size={18} /> Directorio de Equipo
                </button>
                <button onClick={() => setActiveTab('nomina')} className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors border-b-2 whitespace-nowrap ${activeTab === 'nomina' ? 'border-amber-500 text-amber-400' : 'border-transparent text-slate-400 hover:text-slate-300'}`}>
                    <DollarSign size={18} /> Nómina y Pagos
                </button>
            </div>

            {/* TAB: HORARIO MAESTRO */}
            {activeTab === 'horario_maestro' && (
                <div className="space-y-6 animate-in fade-in duration-300">
                    <div className="flex justify-between items-center bg-slate-800/80 p-4 rounded-xl border border-slate-700 mb-6">
                        <h2 className="text-lg font-bold text-white">Plantilla de Operaciones</h2>
                        <p className="text-sm text-slate-400 hidden md:block">Las jornadas cruzan diariamente con la asistencia. El día cierra a las 11:59 PM.</p>
                    </div>

                    {renderHorarioBlock('Atención al Cliente', 'Atención al Cliente', 'cyan')}
                    {renderHorarioBlock('Gestión de Cuentas', 'Gestión de Cuentas', 'indigo')}
                </div>
            )}

            {/* TAB: EQUIPO */}
            {activeTab === 'equipo' && (
                <div className="space-y-6 animate-in fade-in duration-300">
                    <div className="flex justify-between items-center bg-slate-800/80 p-4 rounded-xl border border-slate-700">
                        <h2 className="text-lg font-bold text-white">Directorio del Personal</h2>
                        {!isSupervisor && (
                            <button onClick={handleOpenNewStaffModal} className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2">
                                <PlusCircle size={18} /> Agregar Miembro
                            </button>
                        )}
                    </div>

                    <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-xl overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left text-slate-300">
                                <thead className="bg-slate-900/50 text-slate-400 uppercase text-xs">
                                    <tr>
                                        <th className="px-6 py-4">Nombre</th>
                                        <th className="px-6 py-4">Rol</th>
                                        <th className="px-6 py-4">Salario Base</th>
                                        <th className="px-6 py-4">Email Sistema</th>
                                        <th className="px-6 py-4">Correo Binance</th>
                                        <th className="px-6 py-4 text-center">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700/50">
                                    {personal.length === 0 ? (
                                        <tr><td colSpan="6" className="text-center py-8 text-slate-500">No hay personal registrado</td></tr>
                                    ) : personal.map(p => (
                                        <tr key={p.id} className="hover:bg-slate-700/30">
                                            <td className="px-6 py-4 font-bold text-white">{p.nombre}</td>
                                            <td className="px-6 py-4">{p.rol}</td>
                                            <td className="px-6 py-4 font-mono text-emerald-400">${p.salario_base}</td>
                                            <td className="px-6 py-4 text-slate-400 text-xs">{p.email || 'No asignado'}</td>
                                            <td className="px-6 py-4 text-slate-400 text-xs">{p.correo_binance || 'N/A'}</td>
                                            <td className="px-6 py-4 text-center">
                                                {!isSupervisor && (
                                                    <div className="flex justify-center gap-2">
                                                        <button onClick={() => handleEditStaffClick(p)} className="text-slate-400 hover:text-indigo-400 p-1"><Edit size={18} /></button>
                                                        <button onClick={() => handleDeleteStaff(p.id)} className="text-slate-500 hover:text-red-400 p-1"><Trash2 size={18} /></button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB: NOMINA */}
            {activeTab === 'nomina' && (
                <div className="space-y-6 animate-in fade-in duration-300">
                    <PayrollView 
                        personal={personal} 
                        asistencias={asistencias} 
                        isAdminOrGerente={isAdminOrGerente} 
                    />
                </div>
            )}

            {/* MODAL: NUEVO MIEMBRO */}
            {showStaffModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-slate-800 rounded-2xl w-full max-w-sm border border-slate-700 shadow-2xl animate-in zoom-in-95 duration-200">
                        <div className="p-5 border-b border-slate-700/50 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-white">{editingStaffId ? 'Editar Miembro' : 'Nuevo Miembro'}</h3>
                            <button onClick={() => setShowStaffModal(false)} className="text-slate-400 hover:text-white"><XCircle size={24} /></button>
                        </div>
                        <form onSubmit={handleSaveStaff} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-slate-400 mb-1">Nombre</label>
                                <input required type="text" value={staffNombre} onChange={(e) => setStaffNombre(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none" placeholder="Ej: Guillermo" />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-slate-400 mb-1">Correo Electrónico (Login)</label>
                                <input required type="email" value={staffEmail} onChange={(e) => setStaffEmail(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none" placeholder="usuario@empresa.com" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-1">Cargo / Rol</label>
                                <select value={staffRol} onChange={(e) => setStaffRol(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none">
                                    <option value="Gestor de Cuentas">Gestor de Cuentas</option>
                                    <option value="ATENCIÓN AL CLIENTE">Agente Atención al Cliente</option>
                                    <option value="Supervisor">Supervisor</option>
                                    <option value="Marketing">Marketing</option>
                                    <option value="ADMIN">Administrador</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-1">Departamento</label>
                                <select value={staffDepartamento} onChange={(e) => setStaffDepartamento(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none">
                                    <option value="Atención al Cliente">Atención al Cliente</option>
                                    <option value="Gestión de Cuentas">Gestión de Cuentas</option>
                                    <option value="Gerencia">Gerencia</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-1">Salario Base ($)</label>
                                <input required type="number" min="0" value={staffSalario} onChange={(e) => setStaffSalario(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none" placeholder="100" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-400 mb-1">Bono Aprobado ($)</label>
                                <input type="number" min="0" value={staffBono} onChange={(e) => setStaffBono(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none" placeholder="50" />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-slate-400 mb-1">Correo Binance (Pagos)</label>
                                <input type="email" value={staffCorreoBinance} onChange={(e) => setStaffCorreoBinance(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-emerald-500 focus:outline-none" placeholder="usuario@binance.com" />
                            </div>
                            <div className="pt-4 flex gap-3 md:col-span-2">
                                <button type="button" onClick={() => setShowStaffModal(false)} className="flex-1 px-4 py-2.5 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 transition-colors">Cancelar</button>
                                <button type="submit" className="flex-1 px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium transition-colors">Guardar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StaffDashboard;
